import React from "react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader, CTASection } from "@/components/Sections";
import { Reveal, TechLabel } from "@/components/Reveal";

const DEFINE = [
  "Focusing on the problems and needs of our business partners",
  "Providing customized solutions based on specific operational requirements",
  "Working with reliable manufacturers and suppliers",
  "Using our technical understanding to identify suitable products and parts",
  "Fulfilling our commitments with reliable and responsive service",
  "Providing professional logistics and supply support",
];

export default function About() {
  useSEO({
    title: "About BlackSilex | Mining Equipment Parts",
    description: "About BlackSilex — a Canadian supplier of mining equipment spare parts, wear parts and industrial components, focused on reliable sourcing and competitive pricing.",
    path: "/about",
  });

  return (
    <div data-testid="about-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
        eyebrow="About"
        titleLines={["About", "BlackSilex."]}
      />

      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="about-content">
        <div className="space-y-8 text-mutedgray text-lg leading-relaxed">
          <Reveal>
            <p>
              The purpose of BlackSilex is to supply machinery, equipment, spare parts and consumables to companies operating in the Mining, Construction and Energy sectors.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p>
              BlackSilex provides practical sourcing solutions for businesses operating in demanding environments. By working with a network of manufacturers and suppliers, we help our business partners access the equipment, parts and consumables they need for their operations.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              BlackSilex has positioned itself as a solution-focused supplier by understanding the different challenges faced by businesses in the field. We focus on the specific needs of each business partner rather than promoting a particular product or service.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p>
              All products and solutions provided by BlackSilex are selected according to the individual requirements and operating conditions of each business partner. We work across different product categories and supplier networks to identify solutions that are appropriate for the application, equipment and requirements.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-display text-2xl md:text-3xl font-bold text-offwhite leading-tight">
              Our focus is not simply on selling a product. Our focus is on understanding the problem, finding the right solution and helping our business partners keep their operations running efficiently.
            </p>
          </Reveal>
        </div>

        <div className="mt-16">
          <TechLabel>We Define Ourselves By</TechLabel>
          <ul className="mt-8 border-t border-offwhite/10">
            {DEFINE.map((d, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <li className="flex items-start gap-5 border-b border-offwhite/10 py-5" data-testid={`define-${i}`}>
                  <span className="font-mono-tech text-xs text-strike mt-1">0{i + 1}</span>
                  <span className="text-offwhite text-lg leading-relaxed">{d}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
